import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { BarChart3 } from "lucide-react";

const GRID = "rgba(148,163,184,0.15)";
const TICK = "#94a3b8";
const BAR_BLUE = "#60a5fa";
const BAR_VIOLET = "#a78bfa";
const BAR_AMBER = "#fbbf24";
const BAR_EMERALD = "#34d399";

const DarkTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div className="rounded-md border border-slate-700/60 bg-slate-900/95 px-2.5 py-1.5 text-xs text-slate-200 shadow-lg">
      <div className="font-medium text-slate-100 mb-0.5">{label}</div>
      {payload.map((entry, i) => (
        <div key={i} className="flex items-center gap-2">
          <span
            className="inline-block h-2 w-2 rounded-full"
            style={{ background: entry.color }}
          />
          <span className="text-slate-400">{entry.name}:</span>
          <span className="font-semibold text-slate-100">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

const ChartCard = ({ title, subtitle, children, className = "" }) => (
  <section
    className={`rounded-2xl border border-slate-700/40 bg-slate-800/40 overflow-hidden ${className}`}
  >
    <header className="flex items-baseline justify-between px-4 py-2.5 border-b border-slate-700/40 bg-slate-900/40">
      <h3 className="text-[11px] uppercase tracking-wider font-semibold text-slate-300 flex items-center gap-2">
        <BarChart3 className="h-3.5 w-3.5 text-slate-400" />
        {title}
      </h3>
      {subtitle && <span className="text-[11px] text-slate-500">{subtitle}</span>}
    </header>
    <div className="p-3 h-72">{children}</div>
  </section>
);

const BarChartBlock = ({ data, dataName, color = BAR_BLUE }) => (
  <ResponsiveContainer width="100%" height="100%">
    <BarChart data={data} margin={{ top: 12, right: 12, left: 0, bottom: 6 }}>
      <CartesianGrid strokeDasharray="3 3" stroke={GRID} />
      <XAxis
        dataKey="name"
        tick={{ fontSize: 11, fill: TICK }}
        axisLine={{ stroke: GRID }}
        tickLine={{ stroke: GRID }}
      />
      <YAxis
        tick={{ fontSize: 11, fill: TICK }}
        axisLine={{ stroke: GRID }}
        tickLine={{ stroke: GRID }}
        width={32}
      />
      <Tooltip
        cursor={{ fill: "rgba(148,163,184,0.06)" }}
        content={<DarkTooltip />}
      />
      <Bar dataKey="value" name={dataName} fill={color} radius={[6, 6, 0, 0]} />
    </BarChart>
  </ResponsiveContainer>
);

const Stats = ({ ws, tut, wstutDays }) => {
  const wsdata = ws.map((item) => ({
    name: item.workshop_code,
    value: item.attendees,
  }));
  const tutdata = tut.map((item) => ({
    name: item.tutorial_code,
    value: item.attendees,
  }));

  const wsDays = [
    { name: "Aug 15th", value: wstutDays.ws?.day1 ?? 0 },
    { name: "Aug 16th", value: wstutDays.ws?.day2 ?? 0 },
    { name: "Aug 17th", value: wstutDays.ws?.day3 ?? 0 },
  ];
  const tutDays = [
    { name: "Aug 15th", value: wstutDays.tut?.day1 ?? 0 },
    { name: "Aug 16th", value: wstutDays.tut?.day2 ?? 0 },
    { name: "Aug 17th", value: wstutDays.tut?.day3 ?? 0 },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Workshop attendees per day" subtitle="Bremen">
          <BarChartBlock data={wsDays} dataName="Attendees" color={BAR_BLUE} />
        </ChartCard>
        <ChartCard title="Tutorial attendees per day" subtitle="Bremen">
          <BarChartBlock data={tutDays} dataName="Attendees" color={BAR_VIOLET} />
        </ChartCard>
      </div>

      <ChartCard title="Workshop attendees" subtitle={`${ws.length} workshops`}>
        <BarChartBlock data={wsdata} dataName="Attendees" color={BAR_EMERALD} />
      </ChartCard>

      <ChartCard title="Tutorial attendees" subtitle={`${tut.length} tutorials`}>
        <BarChartBlock data={tutdata} dataName="Attendees" color={BAR_AMBER} />
      </ChartCard>
    </div>
  );
};

export default Stats;
